import { placeSections, RESULT_LAYOUT_COLUMNS, type ResultSectionWidth } from '@shared/result-layout';
import type { DashboardComposition } from '@shared/types/dashboard-composition';
import type { ResultPayload, ResultTabScope } from '@shared/types/result-composition';
import { createViewDefinitionForViewType } from '@shared/types/view-registry';
import {
  DEFAULT_VIEW_SCHEMA_VERSION,
  type AnyViewEnvelope,
  type DataTableRow,
  type ViewType,
} from '@shared/types/views';
import type { ToolRunListItem } from '@/lib/tool-runs';
import type { WorkDocumentOutput } from './work-document-output';

export interface ToolResultTab {
  id: string;
  label: string;
  scope: ResultTabScope;
  composition: DashboardComposition;
  viewEnvelopesByInstanceId: Record<string, AnyViewEnvelope>;
  /** Undefined for legacy single-view results; empty for uneditable composites. */
  documentOutputs?: Record<string, WorkDocumentOutput>;
}

const buildTabComposition = (params: {
  tabLabel: string;
  description?: string | null;
  views: Array<{ instanceId: string; viewType: ViewType; title: string; envelope: AnyViewEnvelope; width?: ResultSectionWidth }>;
}): { composition: DashboardComposition; viewEnvelopesByInstanceId: Record<string, AnyViewEnvelope> } => {
  const viewEnvelopesByInstanceId: Record<string, AnyViewEnvelope> = {};
  const placements = placeSections(params.views.map((view) => view.width));

  const views = params.views.map((view, index) => {
    viewEnvelopesByInstanceId[view.instanceId] = view.envelope;
    return {
      id: view.instanceId,
      viewDefinition: createViewDefinitionForViewType(view.viewType, { title: view.title }),
      placement: { position: placements[index]! },
      previewEnvelope: view.envelope,
      provenance: { type: 'generated' as const, capturedAt: new Date().toISOString() },
    };
  });

  return {
    composition: {
      version: '1.0',
      metadata: {
        title: params.tabLabel,
        description: params.description ?? undefined,
        visibility: 'private',
      },
      layout: { type: 'grid', columns: RESULT_LAYOUT_COLUMNS },
      views,
    },
    viewEnvelopesByInstanceId,
  };
};

/**
 * Aggregated envelope for team/tenant tabs: one row per visible completed run of
 * this tool. Every value comes from real runs (`execution_summary` titles and
 * narrations, real members, real dates) under RLS — arrangement, not fabrication.
 */
const buildAggregateRunsEnvelope = (params: {
  tabLabel: string;
  runs: ToolRunListItem[];
}): AnyViewEnvelope => {
  const completed = params.runs
    .filter((run) => run.status === 'completed')
    .sort((left, right) =>
      (right.completedAt ?? right.updatedAt).localeCompare(left.completedAt ?? left.updatedAt));

  if (completed.length === 0) {
    return {
      view_type: 'fallback_panel',
      schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
      data: {
        title: params.tabLabel,
        message:
          'Aucun résultat partagé pour le moment. Cette vue s’alimentera au fil des prochains runs de l’équipe.',
        reason: 'source_no_data',
      },
    } as AnyViewEnvelope;
  }

  const rows: DataTableRow[] = completed.map((run) => ({
    id: run.id,
    cells: [
      { key: 'title', value: run.title },
      { key: 'owner', value: run.displayName === 'You' ? 'Moi' : run.displayName },
      { key: 'summary', value: run.summary },
      {
        key: 'completed_at',
        value: run.completedAt
          ? new Date(run.completedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
          : null,
      },
    ],
  }));

  return {
    view_type: 'data_table',
    schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
    data: {
      title: params.tabLabel,
      columns: [
        { key: 'title', label: 'Tâche' },
        { key: 'owner', label: 'Par' },
        { key: 'summary', label: 'Résumé' },
        { key: 'completed_at', label: 'Terminé' },
      ],
      rows,
    },
  } as AnyViewEnvelope;
};

/**
 * Builds the result tabs of a run. Composite runs (`result_payload`) yield one tab
 * per perspective with all their materialized sections; legacy single-view runs
 * yield one tab wrapping `view_payload`. Team/tenant tabs are aggregated at read
 * time from the visible runs of the tool (RLS-scoped), never faked from a single run.
 */
export const buildRunResultTabs = (params: {
  title: string;
  description?: string | null;
  resultPayload: ResultPayload | null;
  fallback: { viewType: ViewType; envelope: AnyViewEnvelope } | null;
  /** Visible runs of this tool, used to populate team/tenant aggregate tabs. */
  aggregateRuns?: ToolRunListItem[];
}): ToolResultTab[] => {
  const { resultPayload } = params;

  if (!resultPayload) {
    if (!params.fallback) return [];
    const built = buildTabComposition({
      tabLabel: params.title,
      description: params.description,
      views: [{
        instanceId: 'tool-run-result',
        viewType: params.fallback.viewType,
        title: params.title,
        envelope: params.fallback.envelope,
      }],
    });
    return [{ id: 'result', label: 'Résultat', scope: 'run', ...built }];
  }

  return resultPayload.tabs.map((tab) => {
    const documentOutputs: Record<string, WorkDocumentOutput> = {};
    let views: Array<{ instanceId: string; viewType: ViewType; title: string; envelope: AnyViewEnvelope; width?: ResultSectionWidth }>;
    if (tab.sections.length > 0) {
      views = tab.sections.map((section) => {
        const instanceId = JSON.stringify([tab.id, section.id]);
        if (tab.scope === 'run' && section.viewType === 'artifact_document') {
          documentOutputs[instanceId] = { tabId: tab.id, sectionId: section.id };
        }
        return {
        instanceId,
        viewType: section.viewType,
        title: section.label || params.title,
        envelope: section.envelope,
        ...(section.width ? { width: section.width } : {}),
      }; });
    } else if (tab.scope !== 'run') {
      // Render the perspective the conversation designed: each spec'd section keeps
      // its own label. Tabular sections receive the cross-run aggregate; section
      // types we cannot aggregate yet state it honestly instead of improvising.
      const specs = tab.sectionSpecs ?? [];
      const tabularSpecs = specs.filter(
        (spec) => spec.viewType === 'data_table' || spec.viewType === 'metric_grid'
      );
      const primarySpec = tabularSpecs[0] ?? null;
      const envelope = buildAggregateRunsEnvelope({
        tabLabel: primarySpec?.label ?? tab.label,
        runs: params.aggregateRuns ?? [],
      });
      views = [{
        instanceId: `${tab.id}-aggregate`,
        viewType: envelope.view_type === 'fallback_panel' ? 'fallback_panel' : 'data_table',
        title: primarySpec?.label ?? tab.label,
        envelope,
      }];
      for (const spec of specs) {
        if (spec === primarySpec) continue;
        views.push({
          instanceId: `${tab.id}-${spec.id}-pending`,
          viewType: 'fallback_panel' as ViewType,
          title: spec.label,
          envelope: {
            view_type: 'fallback_panel',
            schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
            data: {
              title: spec.label,
              message:
                'Cette section agrégera les résultats partagés des prochains runs. Elle n’est pas encore alimentée.',
              reason: 'source_no_data',
            },
          } as AnyViewEnvelope,
        });
      }
    } else {
      views = [{
        instanceId: `${tab.id}-pending`,
        viewType: 'fallback_panel' as ViewType,
        title: tab.label,
        envelope: {
          view_type: 'fallback_panel',
          schema_version: DEFAULT_VIEW_SCHEMA_VERSION,
          data: {
            title: tab.label,
            message: 'Aucune section exploitable pour ce run.',
            reason: 'source_no_data',
          },
        } as AnyViewEnvelope,
      }];
    }

    const built = buildTabComposition({
      tabLabel: tab.label,
      description: params.description,
      views,
    });
    return { id: tab.id, label: tab.label, scope: tab.scope, documentOutputs, ...built };
  });
};

/** The scope suffix of a result tab (« · Équipe »), unless the label already says it. */
export const resultTabScopeSuffix = (label: string, scope: ResultTabScope): string => {
  if (scope === 'run') return '';
  const word = scope === 'team' ? 'Équipe' : 'Entreprise';
  return label.toLocaleLowerCase('fr-FR').includes(word.toLocaleLowerCase('fr-FR')) ? '' : ` · ${word}`;
};
